import { useRef, useState } from "react";
import { Autocomplete, Badge } from "reshaped";

import type OutgoingRequest from "@mpa/toolbox/endpoint/OutgoingRequest";

import { useOutgoingRequest } from "../../data/tanstack-query";
import Await from "../async/Await";
import EmptyView from "../EmptyView";
import { Field } from "./Field";
import {
    type AnyFieldProps,
    useFieldDirtyness,
    useFieldId,
    useFieldImperativeHandle,
    useValidity,
} from "./utilities";

export type SearchableSelectFieldProps<T> = AnyFieldProps<T> & {
    placeholder?: string;
    keyExtractor: (item: T) => string;
    labelExtractor: (item: T) => string;
    searchItems: (search: string) => OutgoingRequest<any, any, any, any, Array<T>>;
};

export default function SearchableSelectField<T>(props: SearchableSelectFieldProps<T>) {
    const { value, onChange, placeholder, labelExtractor } = props;
    const id = useFieldId(props);
    const inputRef = useRef<HTMLInputElement>(null);

    const { isDirty, setDirty } = useFieldDirtyness(props);

    const [search, setSearch] = useState<string>("");

    const validity = useValidity(props, []);

    const onItemSelect = (item: T) => {
        setDirty();
        onChange(item);
        setSearch("");
    };

    useFieldImperativeHandle(props, { validity, setDirty, inputRef });

    return (
        <Field {...props} isDirty={isDirty} validity={validity}>
            <Autocomplete
                name={id}
                placeholder={value ? "" : placeholder || ""}
                value={search}
                startSlot={
                    value ? (
                        <Badge
                            key="1"
                            size="small"
                            onDismiss={() => onChange(null)}
                            dismissAriaLabel="Supprimer le destinataire"
                        >
                            {labelExtractor(value)}
                        </Badge>
                    ) : null
                }
                onChange={(e) => setSearch(e.value)}
                onItemSelect={(d) => onItemSelect(d.data as T)}
            >
                {search === "" ? null : (
                    <Await>
                        <SearchableSelectFieldItems {...props} search={search} />
                    </Await>
                )}
            </Autocomplete>
        </Field>
    );
}

type SearchableSelectFieldItemsProps<T> = SearchableSelectFieldProps<T> & { search: string };

function SearchableSelectFieldItems<T>(props: SearchableSelectFieldItemsProps<T>) {
    const { search, searchItems, keyExtractor, labelExtractor } = props;
    const req = searchItems(search);
    const results = useOutgoingRequest(req);
    if (results.length === 0) return <EmptyView message="Aucun résultat trouvé" />;
    return results.map((result) => (
        <Autocomplete.Item key={keyExtractor(result)} value={keyExtractor(result)} data={result}>
            {labelExtractor(result)}
        </Autocomplete.Item>
    ));
}
